import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'

import { cn } from '@/shared/utils/helpers'

import { Integration } from '@core/api/entities/integration'

import IntegrationCard from './IntegrationCard'

export interface IntegrationsCarouselProps {
	integrations: Integration[]
	className?: string
	onScrollStateChange?: (
		canScrollLeft: boolean,
		canScrollRight: boolean
	) => void
}

export interface IntegrationsCarouselRef {
	scrollLeft: () => void
	scrollRight: () => void
	canScrollLeft: boolean
	canScrollRight: boolean
}

const IntegrationsCarousel = React.forwardRef<
	IntegrationsCarouselRef,
	IntegrationsCarouselProps
>(({ integrations, className, onScrollStateChange }, ref) => {
	const scrollContainerRef = useRef<HTMLDivElement>(null)
	const [canScrollLeft, setCanScrollLeft] = useState(false)
	const [canScrollRight, setCanScrollRight] = useState(false)

	const updateScrollState = () => {
		const container = scrollContainerRef.current
		if (!container) return

		const newCanScrollLeft = container.scrollLeft > 0
		const newCanScrollRight =
			container.scrollLeft < container.scrollWidth - container.clientWidth - 10

		setCanScrollLeft(newCanScrollLeft)
		setCanScrollRight(newCanScrollRight)
		onScrollStateChange?.(newCanScrollLeft, newCanScrollRight)
	}

	const scrollByPage = (direction: 'left' | 'right') => {
		const container = scrollContainerRef.current
		if (!container) return

		const amount = container.clientWidth * 0.9
		container.scrollBy({
			left: direction === 'right' ? amount : -amount,
			behavior: 'smooth',
		})
	}

	useImperativeHandle(
		ref,
		() => ({
			scrollLeft: () => scrollByPage('left'),
			scrollRight: () => scrollByPage('right'),
			canScrollLeft,
			canScrollRight,
		}),
		[canScrollLeft, canScrollRight]
	)

	useEffect(() => {
		const container = scrollContainerRef.current
		if (!container) return

		container.addEventListener('scroll', updateScrollState)
		updateScrollState()

		return () => {
			container.removeEventListener('scroll', updateScrollState)
		}
	}, [integrations, onScrollStateChange])

	if (!integrations.length) return null

	return (
		<div className={cn('relative w-full', className)}>
			<div
				ref={scrollContainerRef}
				className={cn(
					'overflow-x-auto overflow-y-hidden py-2',
					'[&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]',
					'snap-x snap-mandatory scroll-smooth'
				)}
			>
				<div className="flex gap-4 min-w-min">
					{integrations.map((integration) => (
						<div
							key={integration.name + integration.type}
							className="shrink-0 flex snap-start"
						>
							<IntegrationCard integration={integration} />
						</div>
					))}
				</div>
			</div>
		</div>
	)
})

IntegrationsCarousel.displayName = 'IntegrationsCarousel'

export default IntegrationsCarousel
